import { useSyncExternalStore } from "react";
import { jwtDecode } from "jwt-decode";
import { SHORTCUT_PAGE_IDS } from "./navIcons";

/** Bump the version segment if the stored shape stops being a plain string[]. */
const STORAGE_PREFIX = "nav:shortcuts:v1";

export const MAX_SHORTCUTS = 10;

let scopeToken: string | null = null;
let scopeValue = "anon";

/** Namespaced per user so a shared machine keeps rows separate. */
function userScope(): string {
  const token = localStorage.getItem("access_token");
  if (token === scopeToken) return scopeValue;
  scopeToken = token;
  scopeValue = "anon";
  if (token) {
    try {
      scopeValue = jwtDecode<{ sub?: string }>(token)?.sub || "anon";
    } catch {
      scopeValue = "anon";
    }
  }
  return scopeValue;
}

function storageKey(): string {
  return `${STORAGE_PREFIX}:${userScope()}`;
}

function read(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    // Absent key = never customised. An empty array is a real choice and stays.
    if (raw === null) return [...SHORTCUT_PAGE_IDS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...SHORTCUT_PAGE_IDS];
    return parsed
      .filter((id): id is string => typeof id === "string")
      .slice(0, MAX_SHORTCUTS);
  } catch {
    return [...SHORTCUT_PAGE_IDS];
  }
}

let cache: string[] | null = null;
let cachedKey: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((listener) => listener());
}

function getSnapshot(): string[] {
  const key = storageKey();
  if (cache === null || cachedKey !== key) {
    cachedKey = key;
    cache = read(key);
  }
  return cache;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === null || event.key === storageKey()) {
      cache = null;
      emit();
    }
  });
}

function write(ids: string[]) {
  cache = ids;
  cachedKey = storageKey();
  try {
    localStorage.setItem(cachedKey, JSON.stringify(ids));
  } catch {
    // Quota or Safari private mode — keep the change in memory for this session.
  }
  emit();
}

/** Ordered page ids currently pinned to the shortcut row. */
export function useShortcutIds(): string[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

function pinShortcut(id: string) {
  const current = getSnapshot();
  if (current.includes(id) || current.length >= MAX_SHORTCUTS) return;
  write([...current, id]);
}

export function unpinShortcut(id: string) {
  const current = getSnapshot();
  if (!current.includes(id)) return;
  write(current.filter((pinned) => pinned !== id));
}

export function toggleShortcut(id: string) {
  if (getSnapshot().includes(id)) unpinShortcut(id);
  else pinShortcut(id);
}

/** Pins hidden by a permission change stay at the tail. */
export function setShortcutOrder(visibleIds: string[]) {
  const current = getSnapshot();
  const next = [
    ...visibleIds,
    ...current.filter((id) => !visibleIds.includes(id)),
  ];
  if (
    next.length === current.length &&
    next.every((id, index) => id === current[index])
  ) {
    return;
  }
  write(next);
}

export function resetShortcuts() {
  try {
    localStorage.removeItem(storageKey());
  } catch {
    // ignore
  }
  cache = null;
  emit();
}
